// Implements: F-DSG-001, F-UIX-020
/**
 * "Duplicar y editar" of the open profile (owner 2026-10-19, answers 4A and 2A): one click sends
 * `POST /design/profiles/{id}/duplicate` and the server stores the copy, "Copia de <name>", with the
 * logo and reference images, in the scope IT decides: the original's when the person may edit it,
 * else the person's personal scope (a profile they only read, the system default). The client sends
 * no scope. The parent then opens the copy, by id
 * (`onDuplicated(sourceId, copyId)`), so it can ignore an answer that arrives after the person moved
 * on (checklist 14).
 *
 * The copy is not idempotent: while the first click is in flight the button is `loading`, which
 * refuses every further click, and the client sends it once (`retry: false`, checklist 13). A refusal (an id already gone, say) is the server's sentence in Spanish, shown as an alert under the button;
 * the original stays open. Mounted with `key` = the profile id, so a refusal never follows the
 * person to another profile.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { useDuplicateProfile } from "../hooks/useDesignProfiles";
import styles from "../components/ProfileEditor.module.css";

export interface ProfileDuplicateContainerProps {
  readonly profileId: string;
  readonly onDuplicated: (sourceId: string, copyId: string) => void;
  /** The read-only view's main action (mockup v3 `btn-primary`); beside the save it is secondary. */
  readonly primary?: boolean | undefined;
}

export function ProfileDuplicateContainer({
  profileId,
  onDuplicated,
  primary = false,
}: ProfileDuplicateContainerProps): ReactElement {
  const { t } = useTranslation();
  const duplicate = useDuplicateProfile();
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const run = async (): Promise<void> => {
    setPending(true);
    setFailure(null);
    const outcome = await duplicate(profileId);
    setPending(false);
    if (!outcome.ok) {
      setFailure(userMessage(outcome.error, t("design.duplicate.failed")));
      return;
    }
    if (outcome.copy !== undefined) onDuplicated(profileId, outcome.copy.id);
  };

  return (
    <>
      <Button
        variant={primary ? "primary" : "secondary"}
        type="button"
        loading={pending}
        onClick={() => {
          void run();
        }}
      >
        <Icon name="copy" size="sm" />
        {t("design.readOnly.duplicate")}
      </Button>
      {failure === null ? null : (
        <p className={styles.error} role="alert">
          {failure}
        </p>
      )}
    </>
  );
}
